import React, { useState } from "react";

import * as Sentry from "@sentry/react";
import { useSearchParams } from "react-router-dom";

import { SelectChangeEvent, Stack } from "@mui/material";

import useConnectCalendar from "@app/analytics/hooks/calendar/useConnectCalendar";
import { updateUserSettings } from "@app/analytics/preferences/api";
import type { CalendarPermissionLevel } from "@app/analytics/types";
import { useAuthenticated } from "@app/common/auth/useAuth";
import useTimeout from "@app/common/hooks/useTimeout";
import ROUTES from "@app/common/routes";
import type { CalendarProviderId } from "@app/common/types/providerTypes";
import CalendarButton from "@app/embedded/zoom/welcome/CalendarButton";
import Expander from "@app/embedded/zoom/welcome/Expander";
import {
  Container,
  Disclaimer,
  Footer,
  Header,
  Section,
  SettingsDescription,
  SettingsToggle,
  SkipButton,
  SubHeader,
} from "@app/embedded/zoom/welcome/styles";

import Dropdown, { DropdownMenuItem } from "@rui/Dropdown";
import { getSelectedFromOptions } from "@rui/Dropdown/utils";
import Link from "@rui/Link";
import LogomarkWithGradient from "@rui/LogomarkWithGradient";
import Notice from "@rui/Notice";
import ProductFeatureList from "@rui/ProductFeatureList";
import ProductFeatureListItem from "@rui/ProductFeatureListItem";

export const MEETING_ROLE = {
  title: "Meeting Role",
  name: "calendarPermissionLevel",
  options: [
    {
      label: "Join all calendar invites",
      value: "all",
    },
    {
      label: "Join events where you are the host",
      value: "hosting_only",
    },
  ],
};

const ConnectCalendar = ({
  calendarError = false,
  handleNextStep,
  returnTo,
}: {
  calendarError?: boolean;
  handleNextStep: () => void;
  returnTo?: string;
}) => {
  const { user, fetchAndSetUser } = useAuthenticated();
  const { connectCalendar } = useConnectCalendar();
  const [queryParams] = useSearchParams();
  const isCalendarError =
    queryParams.get("action")?.includes("calendarError") || calendarError;

  const [isConnecting, setIsConnecting] = useState<CalendarProviderId | null>(
    null
  );

  const willAutoJoinCalendar =
    Boolean(user.calendarPermissionLevel) &&
    user.calendarPermissionLevel !== "invite_only";

  const handleRecordingChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const enabled = event.target.checked;
    try {
      await updateUserSettings({
        automaticRecordingEnabled: enabled,
        transcriptionOptIn: enabled,
        storeRecordingOptIn: enabled,
      });
      await fetchAndSetUser();
    } catch (error: any) {
      Sentry.captureMessage(
        `error updating user settings during zoom onboarding: ${error.message}`
      );
    }
  };

  const handleCalendarToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    const nextCalendarPref = isChecked ? "hosting_only" : "invite_only";
    updateCalendarPref(nextCalendarPref);
  };

  const handleUpdateCalendarPref = (event: SelectChangeEvent) => {
    updateCalendarPref(event.target.value as CalendarPermissionLevel);
  };

  const updateCalendarPref = (nextCalendarPref: CalendarPermissionLevel) => {
    updateUserSettings({ calendarPermissionLevel: nextCalendarPref });
  };

  // Reset the button state after two seconds
  useTimeout(() => setIsConnecting(null), 2000, Boolean(isConnecting));

  const handleConnectCalendar = (
    event: React.MouseEvent<HTMLButtonElement>,
    providerId: CalendarProviderId
  ) => {
    setIsConnecting(providerId);
    connectCalendar(providerId, {
      action: "welcomeCalendarSuccess",
      queryParams: { skipPrefs: true },
      returnTo,
    }).catch(() => {
      // Set to null since we aren't connecting either calendar anymore
      setIsConnecting(null);
      Sentry.captureMessage("failed to connect calendar during onboarding");
    });
  };

  const selectedMeetingRole =
    user.calendarPermissionLevel === "invite_only"
      ? MEETING_ROLE.options[1].value
      : user.calendarPermissionLevel ?? "hosting_only";

  return (
    <Stack
      alignItems="center"
      justifyContent="space-between"
      sx={{ minHeight: { xxs: "100vh", sm: "auto" }, width: "100%" }}
    >
      <Stack sx={{ width: "100%" }}>
        <Container
          direction="column"
          sx={{ width: "100%", padding: "24px 24px 16px" }}
          alignItems="center"
          spacing={2}
        >
          <LogomarkWithGradient />
          <Header>Welcome to Read, {user.firstName}!</Header>
          <SubHeader>
            First things first, link your calendar to have Read join your
            meetings automatically.
          </SubHeader>
          {isCalendarError ? (
            <Notice variant="warning">
              Could not connect your calendar. Please try again.
            </Notice>
          ) : (
            <Notice>
              Connect your busiest calendar (usually work) to get analytics,
              playback, and more across your meetings.
            </Notice>
          )}
        </Container>
        <Container
          direction="column"
          sx={{ width: "100%", pt: 0, pb: 1.5 }}
          alignItems="center"
          spacing={1.5}
        >
          <Stack direction="column" spacing={1.5} sx={{ width: "100%" }}>
            <CalendarButton
              providerId="google_calendar"
              onClick={handleConnectCalendar}
              loading={isConnecting === "google_calendar"}
            />
            <CalendarButton
              providerId="microsoft_graph_calendar"
              onClick={handleConnectCalendar}
              loading={isConnecting === "microsoft_graph_calendar"}
            />
          </Stack>
          <Disclaimer>
            You can adjust your settings or disconnect a calendar at any time by
            visiting{" "}
            <Link href={ROUTES.USER_SETTINGS_INTEGRATIONS}>read.ai</Link>
          </Disclaimer>
        </Container>
      </Stack>
      <Footer>
        <Expander title="Calendar Benefits">
          <Stack spacing={3}>
            <ProductFeatureList>
              <ProductFeatureListItem>
                Read is most impactful when automated with a connected calendar.
              </ProductFeatureListItem>
              <ProductFeatureListItem>
                In-meeting, Read displays a timer, talk time, sentiment, and
                engagement in real-time.
              </ProductFeatureListItem>
              <ProductFeatureListItem>
                After each meeting, receive a transcript with recording,
                summary, key topics, and meeting metrics.
              </ProductFeatureListItem>
            </ProductFeatureList>
            <SkipButton onClick={handleNextStep}>
              Skip calendar connect for now
            </SkipButton>
          </Stack>
        </Expander>
        <Expander title="Settings">
          <Stack spacing="24px">
            <Section sx={{ padding: "8px 12px" }}>
              <SettingsToggle
                checked={user.storeRecordingOptIn}
                onChange={handleRecordingChange}
              >
                Automatically enable recording*
              </SettingsToggle>
              <SettingsDescription>
                Works like magic to enable advanced metric generation,
                transcription, and playback
              </SettingsDescription>
              <Disclaimer
                sx={{
                  textAlign: "left",
                  color: "shades.neutral.500",
                }}
              >
                * Only available when you are the meeting host
              </Disclaimer>
            </Section>
            <Section sx={{ padding: "8px 12px" }}>
              <SettingsToggle
                checked={willAutoJoinCalendar}
                onChange={handleCalendarToggle}
              >
                Automatically join calendar events
              </SettingsToggle>
              <SettingsDescription>
                Allows for hassle-free measurement of future meetings.
              </SettingsDescription>
              <Dropdown
                name={MEETING_ROLE.name}
                sx={{ width: "100%" }}
                selectedItem={getSelectedFromOptions(
                  MEETING_ROLE.options,
                  selectedMeetingRole
                )}
                disabled={!willAutoJoinCalendar}
                onChange={handleUpdateCalendarPref}
              >
                {MEETING_ROLE.options.map((item) => {
                  const isSelectedItem = item.value === selectedMeetingRole;
                  return (
                    <DropdownMenuItem
                      key={item.value}
                      {...item}
                      selected={isSelectedItem}
                    />
                  );
                })}
              </Dropdown>
            </Section>
          </Stack>
        </Expander>
      </Footer>
    </Stack>
  );
};

export default ConnectCalendar;
